import { useCallback } from 'react';
import type { MutableRefObject } from 'react';

interface HostedLiveClientActions {
  sendInput?: (value: string, metadata?: Record<string, unknown>) => void;
  sendReady?: () => void;
}

export interface UseHostedLiveControllerActionsOptions {
  liveClientRef: MutableRefObject<HostedLiveClientActions | null>;
  liveModeActive: boolean;
}

export interface UseHostedLiveControllerActionsReturn {
  handleLiveAvatarModelReady: () => void;
  handleLivePromptOptionSelect: (value: string, label: string) => void;
}

export function useHostedLiveControllerActions({
  liveClientRef,
  liveModeActive
}: UseHostedLiveControllerActionsOptions): UseHostedLiveControllerActionsReturn {
  const handleLiveAvatarModelReady = useCallback(() => {
    if (liveModeActive) {
      console.log('🎭 Live mode avatar ready - sending visual.ready');
      liveClientRef.current?.sendReady?.();
    }
  }, [liveClientRef, liveModeActive]);

  const handleLivePromptOptionSelect = useCallback((value: string, label: string) => {
    // Tag with `source: 'visual-button-tap'` so CamlinVoice can distinguish
    // a deliberate user tap from an ASR-detected speech input when it
    // synthesises the downstream `visual.bargein` (apps/runtime-service/
    // src/server.ts handles `visual.input` and forwards bargein to the
    // voice channel). Useful for analytics ("X% of cancellations come
    // from the visual UI") and for routing rules that should treat a
    // tap differently from a verbal interruption. The runtime ignores
    // unknown metadata keys so this is safe to extend.
    liveClientRef.current?.sendInput?.(value, { label, source: 'visual-button-tap' });
  }, [liveClientRef]);

  return {
    handleLiveAvatarModelReady,
    handleLivePromptOptionSelect
  };
}

export default useHostedLiveControllerActions;
